import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChordTypographicField } from "./ChordTypographicField";
import { ChordVoicingDotSelector } from "./ChordVoicingDotSelector";
import { ChordAtmosphericStaff } from "./ChordAtmosphericStaff";
import { PracticeSurface } from "../practice/PracticeSurface";
import { TaskCreatorButton } from "../TaskCreatorButton";
import { serializeChordState } from "../../lib/taskEngine";
import { buildChordStaffEvents } from "../../lib/chordStaffEvents";
import { INVERSION_LABEL_ES, VOICING_TYPE_LABEL_ES } from "../../lib/chordPedagogy";
import type { NoteName, NeckPosition } from "../../lib/guitarEngine";
import type {
  ChordVoicingResult,
  ExtendedChordQuality,
  ExplorerFilter,
  InversionType,
  VoicingMode,
} from "../../lib/chordEngine";
import type { ChordDefinition } from "../../lib/chordEngine";

/** Ancho fijo del carril de selectores (CAGED variable hace scroll interno). */
const THEORY_CONTROLS_SLOT_PX = 720;
/** Ancho fijo del carril pentagrama + fórmula — no se mueve al cambiar calidad/formas. */
const THEORY_STAFF_SLOT_PX = 420;

export interface ChordImmersiveCanvasProps {
  root: NoteName;
  quality: ExtendedChordQuality;
  def: ChordDefinition;
  inversion: InversionType;
  voicingType: ExplorerFilter["voicingType"];
  mode: VoicingMode;
  currentVoicing: ChordVoicingResult | null;
  voicings: ChordVoicingResult[];
  selectedVoicingIndex: number;
  onSelectVoicing: (index: number) => void;
  mutedStrings: number[];
  stringOrder: "tab" | "physical";
  leftHanded: boolean;
  onStringOrderToggle: () => void;
  onLeftHandedToggle: () => void;
  onNextNote: () => void;
  onNextQuality: () => void;
  onNextInversion: () => void;
  onCycleVoicingType: () => void;
  onFretClick: (stringNum: number, fret: number, note: NoteName) => void;
  onPlayChord?: () => void;
}

export const ChordImmersiveCanvas: React.FC<ChordImmersiveCanvasProps> = ({
  root,
  quality,
  def,
  inversion,
  voicingType,
  mode,
  currentVoicing,
  voicings,
  selectedVoicingIndex,
  onSelectVoicing,
  mutedStrings,
  stringOrder,
  leftHanded,
  onStringOrderToggle,
  onLeftHandedToggle,
  onNextNote,
  onNextQuality,
  onNextInversion,
  onCycleVoicingType,
  onFretClick,
  onPlayChord,
}) => {
  const fretboardRef = useRef<HTMLDivElement>(null);
  const [boardWidth, setBoardWidth] = useState(1400);

  const staffEvents = useMemo(
    () => buildChordStaffEvents(root, quality, inversion, voicingType),
    [root, quality, inversion, voicingType]
  );

  const highlightedPositions = useMemo((): NeckPosition[] => {
    if (!currentVoicing) return [];
    return currentVoicing.positions.filter((p) => !mutedStrings.includes(p.string));
  }, [currentVoicing, mutedStrings]);

  const formulaLine = def.degreeNames.join(" - ");
  const notesLine = staffEvents.map((e) => e.step).join(" - ");
  const title = `${root} ${def.label}`;

  const measureBoard = useCallback(() => {
    const el = fretboardRef.current;
    if (!el) return;
    setBoardWidth(Math.max(640, el.clientWidth - 16));
  }, []);

  useEffect(() => {
    measureBoard();
    const el = fretboardRef.current;
    if (!el) return;
    const ro = new ResizeObserver(measureBoard);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measureBoard]);

  return (
    <div className="h-[calc(100dvh-5rem)] min-h-0 flex flex-col overflow-hidden overflow-x-hidden w-full max-w-none flex-1 box-border pt-28 px-12">
      <div className="shrink-0 flex justify-between items-baseline w-full border-b border-white/5 pb-3 mb-4">
        <h1 className="text-6xl sm:text-7xl font-serif text-white/95 leading-none tracking-tight">
          {title}
        </h1>
        <div className="flex items-center gap-6 shrink-0">
          {onPlayChord && (
            <button
              type="button"
              onClick={onPlayChord}
              className="text-sm font-sans uppercase tracking-widest text-white/40 hover:text-emerald-300 transition-colors"
            >
              Escuchar
            </button>
          )}
          <TaskCreatorButton
            type="chord"
            variant="immersive"
            title={`Acorde ${title}`}
            description={`Practica el acorde ${title} en posición ${mode === "classic" ? "clásica" : "explorador"}`}
            config={serializeChordState(root, quality, mode, selectedVoicingIndex, "chord")}
          />
        </div>
      </div>

      <div
        className="shrink-0 grid w-full mt-2 mb-8 items-center gap-x-10 min-h-[9rem] overflow-visible"
        style={{
          gridTemplateColumns: `${THEORY_CONTROLS_SLOT_PX}px ${THEORY_STAFF_SLOT_PX}px`,
        }}
      >
        <div
          className="flex flex-row items-center gap-8 overflow-x-auto overflow-y-hidden scrollbar-hide min-h-[4.5rem]"
          style={{ width: THEORY_CONTROLS_SLOT_PX, maxWidth: THEORY_CONTROLS_SLOT_PX }}
        >
          <ChordTypographicField label="Raíz" value={root} onClick={onNextNote} />
          <ChordTypographicField label="Especie" value={def.label} onClick={onNextQuality} />
          <ChordTypographicField
            label="Voicing"
            value={VOICING_TYPE_LABEL_ES[voicingType]}
            onClick={onCycleVoicingType}
          />
          <ChordTypographicField
            label="Inversión"
            value={INVERSION_LABEL_ES[inversion]}
            onClick={onNextInversion}
          />
          <ChordVoicingDotSelector
            inline
            voicings={voicings}
            selectedIndex={selectedVoicingIndex}
            onSelect={onSelectVoicing}
          />
        </div>

        <div
          className="flex flex-row items-center gap-12 overflow-visible justify-start"
          style={{ width: THEORY_STAFF_SLOT_PX, maxWidth: THEORY_STAFF_SLOT_PX }}
        >
          <div className="h-36 w-[280px] shrink-0 flex items-center overflow-visible">
            <div className="h-32 w-[186px] scale-150 origin-left opacity-50 transition-opacity duration-500 ease-out hover:opacity-70">
              <ChordAtmosphericStaff events={staffEvents} className="w-full h-full" />
            </div>
          </div>
          <div className="flex flex-col justify-center text-right text-xs text-white/40 font-mono tracking-widest gap-0.5 shrink-0 min-w-[5.5rem]">
            <span>Fórmula: {formulaLine}</span>
            <span>Notas: {notesLine}</span>
          </div>
        </div>
      </div>

      <div ref={fretboardRef} className="flex-1 w-full min-h-0 relative flex flex-col">
        {currentVoicing ? (
          <PracticeSurface
            className="w-full h-full min-h-0 max-w-none"
            highlightedPositions={highlightedPositions}
            mutedStrings={mutedStrings}
            barre={currentVoicing.barre ?? null}
            stringOrder={stringOrder}
            leftHanded={leftHanded}
            showNoteNames
            showFingerBadges
            interactive
            onFretClick={onFretClick}
            visualStyle="wireframe"
            sizeTier="monumental"
            boardWidthOverride={boardWidth}
            chordNutTypography
            exploreChordNodes
          />
        ) : (
          <div className="h-full flex items-center justify-center text-white/40 font-sans text-sm px-4 text-center">
            No hay voicings con estos filtros. Prueba modo clásico o relaja el explorador.
          </div>
        )}

        <div className="absolute top-2 right-0 flex flex-col gap-1.5 z-10">
          <button
            type="button"
            onClick={onStringOrderToggle}
            className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-sans transition-all bg-black/35 backdrop-blur-md border border-white/12 text-slate-100 hover:bg-white/10"
            title={stringOrder === "physical" ? "Orden físico" : "Orden tab"}
          >
            {stringOrder === "physical" ? "|||" : "≡"}
          </button>
          <button
            type="button"
            onClick={onLeftHandedToggle}
            className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-sans transition-all border ${
              leftHanded
                ? "bg-emerald-400/20 text-emerald-200 border-emerald-400/40"
                : "bg-black/35 border-white/12 text-slate-100 hover:bg-white/10"
            }`}
            title={leftHanded ? "Zurdo" : "Diestro"}
          >
            {leftHanded ? "←" : "→"}
          </button>
        </div>
      </div>
    </div>
  );
};
